/**
 * Project service - Firestore CRUD operations for projects
 * Path: users/{userId}/projects/{projectId}
 * Follows ARCH-003: Projects nested under users
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  getDoc,
  query,
  orderBy,
  serverTimestamp,
  writeBatch,
  increment,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Project } from '../types';

/**
 * Create a new project
 */
export async function createProject(
  userId: string,
  data: {
    name: string;
  }
): Promise<string> {
  const projectsRef = collection(db, `users/${userId}/projects`);

  const projectData = {
    userId,
    name: data.name,
    description: '',
    status: 'active' as const,
    viewCount: 0,
    createdAt: serverTimestamp(),
  };

  const docRef = await addDoc(projectsRef, projectData);
  return docRef.id;
}

/**
 * Get all projects for a user
 */
export async function getProjects(userId: string): Promise<Project[]> {
  const projectsRef = collection(db, `users/${userId}/projects`);
  const q = query(projectsRef, orderBy('createdAt', 'desc'));

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    projectId: doc.id,
    ...doc.data(),
  })) as Project[];
}

/**
 * Get a single project by ID
 */
export async function getProjectById(
  userId: string,
  projectId: string
): Promise<Project | null> {
  const projectRef = doc(db, `users/${userId}/projects/${projectId}`);
  const snapshot = await getDoc(projectRef);

  if (!snapshot.exists()) {
    return null;
  }

  return {
    projectId: snapshot.id,
    ...snapshot.data(),
  } as Project;
}

/**
 * Update project
 */
export async function updateProject(
  userId: string,
  projectId: string,
  data: Partial<Pick<Project, 'name' | 'description' | 'status'>>
): Promise<void> {
  const projectRef = doc(db, `users/${userId}/projects/${projectId}`);
  await updateDoc(projectRef, data);
}

/**
 * Delete project and all its views (cascade)
 * Follows DATA-002: View deletion cascade
 */
export async function deleteProject(
  userId: string,
  projectId: string
): Promise<void> {
  const batch = writeBatch(db);

  // Delete all views under this project
  const viewsRef = collection(db, `users/${userId}/projects/${projectId}/views`);
  const viewsSnapshot = await getDocs(viewsRef);

  viewsSnapshot.docs.forEach((viewDoc) => {
    batch.delete(viewDoc.ref);
  });

  // Delete the project itself
  const projectRef = doc(db, `users/${userId}/projects/${projectId}`);
  batch.delete(projectRef);

  await batch.commit();

  // Note: Photo deletion will be handled by Cloud Function (DATA-002)
  // Cloud Function listens to view deletion and deletes all associated photos
}

/**
 * Increment view count for a project
 */
export async function incrementViewCount(
  userId: string,
  projectId: string
): Promise<void> {
  const projectRef = doc(db, `users/${userId}/projects/${projectId}`);
  await updateDoc(projectRef, { viewCount: increment(1) });
}

/**
 * Decrement view count for a project
 */
export async function decrementViewCount(
  userId: string,
  projectId: string
): Promise<void> {
  const projectRef = doc(db, `users/${userId}/projects/${projectId}`);
  await updateDoc(projectRef, { viewCount: increment(-1) });
}
